/**
 * Centralised SVG `<symbol>` library for plant pins.
 *
 * Mounted once at the app root via <MapSpritesGlobal /> so any SVG in the
 * document can `<use href="#mpXxx" />` without re-declaring the defs.
 * Stroke palette: soil #5d4a3a; greens #7a8c6e / #a8b89a / #c8d4a8;
 * blooms #d4a8a8 / #d4a8c8 / #a08bb6 / #e8c060; terracotta #c8604a / #d8884a.
 */
export function MapSpritesDefs() {
  return (
    <>
      {/* ===== TREES ===== */}
      <symbol id="mpTree" viewBox="-50 -50 100 100">
        <path
          d="M -30 0 Q -30 -28 0 -32 Q 30 -30 32 -2 Q 30 28 0 32 Q -30 30 -32 2 Z"
          fill="#c8d4a8"
          fillOpacity="0.5"
          stroke="#5d4a3a"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path
          d="M -20 -10 Q -10 -22 5 -18 M 10 -25 Q 22 -15 25 0 M 20 12 Q 10 25 -5 22 M -22 8 Q -28 -5 -22 -15"
          fill="none"
          stroke="#5d4a3a"
          strokeWidth="0.7"
          strokeOpacity="0.6"
          strokeLinecap="round"
        />
        <line x1="-5" y1="0" x2="5" y2="0" stroke="#5d4a3a" strokeWidth="1.6" />
        <line x1="0" y1="-5" x2="0" y2="5" stroke="#5d4a3a" strokeWidth="1.6" />
      </symbol>

      <symbol id="mpTree_conifer" viewBox="-30 -40 60 80">
        <path
          d="M 0 -36 L -16 -10 L -8 -10 L -22 8 L -10 8 L -26 26 L 26 26 L 10 8 L 22 8 L 8 -10 L 16 -10 Z"
          fill="#7a8c6e"
          fillOpacity="0.6"
          stroke="#5d4a3a"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <line x1="-3" y1="26" x2="-3" y2="34" stroke="#5d4a3a" strokeWidth="1.4" />
        <line x1="3" y1="26" x2="3" y2="34" stroke="#5d4a3a" strokeWidth="1.4" />
      </symbol>

      <symbol id="mpTree_fruit" viewBox="-30 -32 60 64">
        <path
          d="M -22 0 Q -24 -22 0 -26 Q 24 -22 24 0 Q 22 22 0 24 Q -22 22 -22 0 Z"
          fill="#a8b89a"
          fillOpacity="0.6"
          stroke="#5d4a3a"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <circle cx="-9" cy="-7" r="3" fill="#c8604a" stroke="#5d4a3a" strokeWidth="0.6" />
        <circle cx="6" cy="-3" r="3" fill="#c8604a" stroke="#5d4a3a" strokeWidth="0.6" />
        <circle cx="-2" cy="11" r="3" fill="#c8604a" stroke="#5d4a3a" strokeWidth="0.6" />
        <circle cx="13" cy="9" r="3" fill="#c8604a" stroke="#5d4a3a" strokeWidth="0.6" />
        <line x1="-3" y1="24" x2="-3" y2="30" stroke="#5d4a3a" strokeWidth="1.2" />
        <line x1="3" y1="24" x2="3" y2="30" stroke="#5d4a3a" strokeWidth="1.2" />
      </symbol>

      {/* ===== SHRUBS ===== */}
      <symbol id="mpShrub" viewBox="-30 -30 60 60">
        <path
          d="M -18 0 Q -20 -16 0 -20 Q 20 -18 22 0 Q 20 18 0 22 Q -20 20 -18 4 Z"
          fill="#a8b89a"
          fillOpacity="0.45"
          stroke="#5d4a3a"
          strokeWidth="1.2"
        />
        <path
          d="M -12 -6 Q -4 -14 4 -10 M 8 -12 Q 16 -4 14 6 M -8 8 Q -16 0 -14 -8"
          fill="none"
          stroke="#5d4a3a"
          strokeWidth="0.6"
          strokeOpacity="0.5"
        />
        <line x1="-3" y1="0" x2="3" y2="0" stroke="#5d4a3a" strokeWidth="1" />
        <line x1="0" y1="-3" x2="0" y2="3" stroke="#5d4a3a" strokeWidth="1" />
      </symbol>

      <symbol id="mpShrub_topiary" viewBox="-20 -25 40 50">
        <circle cx="0" cy="-8" r="14" fill="#a8b89a" fillOpacity="0.6" stroke="#5d4a3a" strokeWidth="1.2" />
        <path
          d="M -8 -12 Q -4 -18 4 -14 M 6 -2 Q 12 -4 10 -10 M -10 -4 Q -14 -10 -8 -14"
          fill="none"
          stroke="#5d4a3a"
          strokeWidth="0.6"
          strokeOpacity="0.5"
        />
        <line x1="0" y1="6" x2="0" y2="20" stroke="#5d4a3a" strokeWidth="1.4" />
        <ellipse cx="0" cy="22" rx="6" ry="2" fill="#8a7050" fillOpacity="0.7" stroke="#5d4a3a" strokeWidth="0.8" />
      </symbol>

      <symbol id="mpBamboo" viewBox="-16 -25 32 50">
        <line x1="-8" y1="22" x2="-8" y2="-22" stroke="#7a8c6e" strokeWidth="2.2" />
        <line x1="0" y1="22" x2="0" y2="-22" stroke="#7a8c6e" strokeWidth="2.4" />
        <line x1="8" y1="22" x2="8" y2="-22" stroke="#7a8c6e" strokeWidth="2.2" />
        <line x1="-9.5" y1="-8" x2="-6.5" y2="-8" stroke="#5d4a3a" strokeWidth="0.8" />
        <line x1="-1.5" y1="-2" x2="1.5" y2="-2" stroke="#5d4a3a" strokeWidth="0.8" />
        <line x1="6.5" y1="6" x2="9.5" y2="6" stroke="#5d4a3a" strokeWidth="0.8" />
        <path
          d="M -8 -16 Q -16 -18 -16 -22 M 0 -14 Q 8 -16 9 -22 M 8 -2 Q 16 -4 16 -8"
          fill="none"
          stroke="#7a8c6e"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </symbol>

      {/* ===== FLOWERS ===== */}
      <symbol id="mpRose" viewBox="-12 -12 24 24">
        <circle r="9" fill="#d4a8a8" fillOpacity="0.7" stroke="#5d4a3a" strokeWidth="1" />
        <circle r="4" fill="#c89090" />
        <circle r="1.5" fill="#a06070" />
      </symbol>

      <symbol id="mpDaisy" viewBox="-14 -14 28 28">
        <g stroke="#5d4a3a" strokeWidth="0.6" fill="#f5ecd5">
          <ellipse cx="0" cy="-9" rx="3" ry="5" />
          <ellipse cx="0" cy="-9" rx="3" ry="5" transform="rotate(72)" />
          <ellipse cx="0" cy="-9" rx="3" ry="5" transform="rotate(144)" />
          <ellipse cx="0" cy="-9" rx="3" ry="5" transform="rotate(216)" />
          <ellipse cx="0" cy="-9" rx="3" ry="5" transform="rotate(288)" />
        </g>
        <circle r="3.5" fill="#e8c060" stroke="#5d4a3a" strokeWidth="0.6" />
      </symbol>

      <symbol id="mpTulip" viewBox="-10 -16 20 32">
        <path
          d="M 0 -14 Q -8 -14 -7 -2 Q -7 4 -2 4 L 0 -2 L 2 4 Q 7 4 7 -2 Q 8 -14 0 -14 Z"
          fill="#d4a8a8"
          fillOpacity="0.85"
          stroke="#5d4a3a"
          strokeWidth="1"
          strokeLinejoin="round"
        />
        <path d="M 0 -14 L -2 -6 M 0 -14 L 2 -6" stroke="#5d4a3a" strokeWidth="0.5" opacity="0.6" />
        <line x1="0" y1="4" x2="0" y2="14" stroke="#7a8c6e" strokeWidth="1.4" />
        <path d="M 0 9 Q 6 7 7 12" fill="none" stroke="#7a8c6e" strokeWidth="1.2" strokeLinecap="round" />
      </symbol>

      <symbol id="mpSunflower" viewBox="-15 -15 30 30">
        <g fill="#e8c060" stroke="#5d4a3a" strokeWidth="0.5">
          <ellipse rx="2.4" ry="6" cy="-9" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(45)" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(90)" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(135)" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(180)" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(225)" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(270)" />
          <ellipse rx="2.4" ry="6" cy="-9" transform="rotate(315)" />
        </g>
        <circle r="4" fill="#6a4a30" stroke="#5d4a3a" strokeWidth="0.6" />
        <circle r="1" fill="#3d2818" />
      </symbol>

      <symbol id="mpLavender" viewBox="-12 -19 24 38">
        <g stroke="#7a8c6e" strokeWidth="1" strokeLinecap="round">
          <line x1="-7" y1="15" x2="-5" y2="-2" />
          <line x1="0" y1="15" x2="0" y2="-6" />
          <line x1="7" y1="15" x2="5" y2="-2" />
        </g>
        <g fill="#a08bb6" stroke="#5d4a3a" strokeWidth="0.4">
          <circle cx="-5" cy="-2" r="1.5" />
          <circle cx="-5" cy="-6" r="1.5" />
          <circle cx="-5" cy="-10" r="1.5" />
          <circle cx="0" cy="-6" r="1.5" />
          <circle cx="0" cy="-10" r="1.5" />
          <circle cx="0" cy="-14" r="1.5" />
          <circle cx="5" cy="-2" r="1.5" />
          <circle cx="5" cy="-6" r="1.5" />
          <circle cx="5" cy="-10" r="1.5" />
        </g>
      </symbol>

      <symbol id="mpFoxglove" viewBox="-10 -22 20 44">
        <line x1="0" y1="20" x2="0" y2="-14" stroke="#7a8c6e" strokeWidth="1.4" />
        <g fill="#d4a8c8" stroke="#5d4a3a" strokeWidth="0.5">
          <ellipse cx="-4" cy="0" rx="3.6" ry="2.5" transform="rotate(-25 -4 0)" />
          <ellipse cx="4" cy="4" rx="3.6" ry="2.5" transform="rotate(25 4 4)" />
          <ellipse cx="-4" cy="-6" rx="3" ry="2.1" transform="rotate(-25 -4 -6)" />
          <ellipse cx="4" cy="-2" rx="3" ry="2.1" transform="rotate(25 4 -2)" />
          <ellipse cx="-3" cy="-12" rx="2.4" ry="1.7" transform="rotate(-25 -3 -12)" />
          <ellipse cx="3" cy="-8" rx="2.4" ry="1.7" transform="rotate(25 3 -8)" />
          <ellipse cx="0" cy="-16" rx="2" ry="1.4" />
        </g>
        <path d="M -3 18 Q -10 16 -10 20 M 3 18 Q 10 16 10 20" fill="none" stroke="#7a8c6e" strokeWidth="1" strokeLinecap="round" />
      </symbol>

      {/* ===== CLIMBERS ===== */}
      <symbol id="mpVine" viewBox="-15 -15 30 30">
        <path
          d="M -12 12 C -8 6 -2 4 0 0 C 4 -6 10 -10 8 -12 C 6 -14 2 -10 4 -6 C 6 -2 8 0 10 -2"
          fill="none"
          stroke="#7a8c6e"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path d="M -2 -2 Q -8 -4 -12 -10 Q -8 -6 -2 -2 Z" fill="#a8b89a" fillOpacity="0.7" stroke="#5d4a3a" strokeWidth="0.7" />
        <path d="M 6 4 Q 12 4 14 8 Q 10 6 6 4 Z" fill="#a8b89a" fillOpacity="0.7" stroke="#5d4a3a" strokeWidth="0.7" />
      </symbol>

      <symbol id="mpClimber" viewBox="-15 -15 30 30">
        <path d="M -12 12 L 12 -12 M -12 -12 L 12 12" stroke="#8a7050" strokeWidth="0.7" opacity="0.6" />
        <path d="M -12 12 Q -8 0 -4 -10 Q 4 -2 12 -12" fill="none" stroke="#7a8c6e" strokeWidth="1.6" strokeLinecap="round" />
        <circle cx="-6" cy="0" r="3" fill="#d4a8a8" stroke="#5d4a3a" strokeWidth="0.6" />
        <circle cx="6" cy="-8" r="3" fill="#d4a8a8" stroke="#5d4a3a" strokeWidth="0.6" />
        <circle cx="-2" cy="9" r="3" fill="#d4a8a8" stroke="#5d4a3a" strokeWidth="0.6" />
      </symbol>

      {/* ===== EDIBLES ===== */}
      <symbol id="mpVeg" viewBox="-10 -10 20 20">
        <rect x="-7" y="-7" width="14" height="14" rx="1.5" fill="#c8d4a8" fillOpacity="0.7" stroke="#5d4a3a" strokeWidth="1" />
        <line x1="-4" y1="0" x2="4" y2="0" stroke="#5d4a3a" strokeWidth="0.8" />
        <line x1="0" y1="-4" x2="0" y2="4" stroke="#5d4a3a" strokeWidth="0.8" />
      </symbol>

      <symbol id="mpCarrot" viewBox="-10 -16 20 32">
        <path d="M -5 -1 L 5 -1 L 0 14 Z" fill="#d8884a" fillOpacity="0.9" stroke="#5d4a3a" strokeWidth="1" strokeLinejoin="round" />
        <line x1="-3" y1="3" x2="3" y2="3" stroke="#5d4a3a" strokeWidth="0.5" opacity="0.6" />
        <line x1="-2" y1="7" x2="2" y2="7" stroke="#5d4a3a" strokeWidth="0.5" opacity="0.6" />
        <path
          d="M 0 -1 Q -6 -7 -8 -14 M 0 -1 Q 0 -9 0 -14 M 0 -1 Q 6 -7 8 -14"
          fill="none"
          stroke="#7a8c6e"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
      </symbol>

      <symbol id="mpTomato" viewBox="-12 -12 24 24">
        <circle r="9" fill="#c8604a" fillOpacity="0.9" stroke="#5d4a3a" strokeWidth="1" />
        <path
          d="M -3 -8 L 0 -10 L 3 -8 L 5 -9 L 3 -6 L 6 -4 L 3 -5 L 0 -8 L -3 -5 L -6 -4 L -3 -6 L -5 -9 Z"
          fill="#7a8c6e"
          stroke="#5d4a3a"
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        <line x1="0" y1="-10" x2="0" y2="-12" stroke="#5d4a3a" strokeWidth="1" />
        <ellipse cx="-3" cy="-2" rx="2.5" ry="1.2" fill="#fff" opacity="0.35" />
      </symbol>

      <symbol id="mpHerb" viewBox="-13 -11 26 22">
        <g fill="#a8b89a" fillOpacity="0.75" stroke="#5d4a3a" strokeWidth="0.6">
          <ellipse cx="-7" cy="3" rx="5" ry="6" />
          <ellipse cx="0" cy="0" rx="6" ry="7" />
          <ellipse cx="7" cy="3" rx="5" ry="6" />
        </g>
        <path
          d="M -6 -1 L -6 -5 M 0 -3 L 0 -8 M 6 -1 L 6 -5"
          stroke="#5d4a3a"
          strokeWidth="0.6"
          opacity="0.6"
          strokeLinecap="round"
        />
      </symbol>

      {/* ===== OTHER ===== */}
      <symbol id="mpFern" viewBox="-12 -19 24 38">
        <path d="M 0 17 Q -2 0 -4 -16" fill="none" stroke="#7a8c6e" strokeWidth="1.4" strokeLinecap="round" />
        <g stroke="#7a8c6e" strokeWidth="1" strokeLinecap="round" fill="none">
          <path d="M -3 -13 Q -8 -13 -10 -16" />
          <path d="M -2 -8 Q -7 -7 -10 -9" />
          <path d="M -1 -3 Q -7 -1 -10 -2" />
          <path d="M 0 1 Q -6 3 -9 5" />
          <path d="M 1 6 Q -4 8 -7 11" />
          <path d="M -3 -13 Q 2 -13 4 -16" />
          <path d="M -2 -8 Q 3 -7 6 -9" />
          <path d="M -1 -3 Q 5 -1 8 -2" />
          <path d="M 0 1 Q 6 3 9 5" />
          <path d="M 1 6 Q 5 8 8 11" />
        </g>
      </symbol>

      <symbol id="mpContainer" viewBox="-12 -12 24 24">
        <path
          d="M -8 -6 L 8 -6 L 6 8 L -6 8 Z"
          fill="#c89070"
          fillOpacity="0.6"
          stroke="#5d4a3a"
          strokeWidth="1.2"
        />
        <line x1="-3" y1="-1" x2="3" y2="-1" stroke="#5d4a3a" strokeWidth="0.8" />
        <line x1="0" y1="-4" x2="0" y2="2" stroke="#5d4a3a" strokeWidth="0.8" />
      </symbol>
    </>
  );
}

/** Hidden SVG that mounts once at the app root so other SVGs can `<use>` these symbols. */
export function MapSpritesGlobal() {
  return (
    <svg
      width="0"
      height="0"
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
      aria-hidden="true"
    >
      <defs>
        <MapSpritesDefs />
      </defs>
    </svg>
  );
}
